// App.jsx
import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Provider } from 'react-redux';
import {
  About, Footer, Home, Projects, Testimonials,
} from './containers';
import Splash from './components/Splash/Splash';
import store from './redux/configureStore';
import { images } from './constants';
import './App.scss';

// Never hold the splash longer than this, even on a very slow connection.
const MAX_SPLASH_MS = 2500;

const SECTIONS = [
  { id: 'home', label: 'Home', Component: Home },
  { id: 'about', label: 'About', Component: About },
  { id: 'projects', label: 'Projects', Component: Projects },
  { id: 'testimonials', label: 'Testimonials', Component: Testimonials },
  { id: 'contact', label: 'Contact', Component: Footer },
];

const App = () => {
  const [splashDone, setSplashDone] = useState(false);
  const [heroReady, setHeroReady] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);
  const sectionRefs = useRef([]);

  // Preload + decode the hero portrait while the splash plays, so it is
  // already in the cache when Home mounts and renders instantly.
  useEffect(() => {
    let cancelled = false;
    const markReady = () => { if (!cancelled) setHeroReady(true); };
    const img = new Image();
    img.src = images.collinsprofileWebp;
    (img.decode ? img.decode() : Promise.resolve()).then(markReady, markReady);
    const fallback = setTimeout(markReady, MAX_SPLASH_MS);
    return () => { cancelled = true; clearTimeout(fallback); };
  }, []);

  const showSplash = !(splashDone && heroReady);

  // Bound directly via onScroll: an effect keyed on showSplash ran while the
  // splash was still exiting (AnimatePresence mode="wait"), before this
  // container existed, so the nav dots and progress bar never updated.
  const handleScroll = (e) => {
    const container = e.currentTarget;
    const idx = Math.round(container.scrollTop / container.clientHeight);
    setActiveIndex(Math.max(0, Math.min(idx, SECTIONS.length - 1)));
  };

  const scrollToSection = (idx) => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTo({
      top: idx * container.clientHeight,
      behavior: 'smooth',
    });
  };

  return (
    <Provider store={store}>
      <AnimatePresence mode="wait">
        {showSplash ? (
          <Splash key="splash" onComplete={() => setSplashDone(true)} />
        ) : (
          // Mounts at full opacity — Home owns its own (short) entrance animations.
          <div key="app" className="relative bg-[#0a0907]">

            {/* ── Progress bar ── */}
            <div className="fixed top-0 left-0 w-full h-0.5 z-50 bg-white/10">
              <motion.div
                className="h-full bg-white origin-left"
                animate={{ scaleX: (activeIndex + 1) / SECTIONS.length }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>

            {/* ── Snap scroll container ── */}
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="h-screen overflow-y-scroll snap-y snap-mandatory"
            >
              {SECTIONS.map(({ id, Component }, idx) => (
                <section
                  key={id}
                  id={id}
                  ref={(el) => { sectionRefs.current[idx] = el; }}
                  // Sections are always visible — CSS snap handles visibility,
                  // not JS animation. Framer Motion wrappers here were previously
                  // hiding non-active sections before they could lay out,
                  // causing blank pages on scroll.
                  className="snap-start h-screen w-full overflow-hidden relative"
                >
                  <Component />
                </section>
              ))}
            </div>

            {/* ── Side nav dots ── */}
            <nav
              // Hidden on phones, where it sat on top of section content
              className="fixed right-6 top-1/2 -translate-y-1/2 z-50 hidden md:flex flex-col items-center gap-4"
              aria-label="Section navigation"
            >
              {SECTIONS.map(({ label }, idx) => (
                <button
                  type="button"
                  key={label}
                  onClick={() => scrollToSection(idx)}
                  aria-label={`Go to ${label}`}
                  className="group relative flex items-center justify-end"
                >
                  <span className="
                    absolute right-full mr-3 px-2 py-1 rounded
                    text-xs font-medium whitespace-nowrap
                    bg-black/80 text-white pointer-events-none
                    opacity-0 translate-x-2
                    group-hover:opacity-100 group-hover:translate-x-0
                    transition-all duration-200
                  "
                  >
                    {label}
                  </span>

                  <motion.span
                    className="block rounded-full border-2"
                    animate={
                      idx === activeIndex
                        ? {
                          width: 12,
                          height: 12,
                          backgroundColor: 'rgba(255,255,255,1)',
                          borderColor: 'rgba(255,255,255,1)',
                          scale: 1.15,
                        }
                        : {
                          width: 10,
                          height: 10,
                          backgroundColor: 'rgba(255,255,255,0)',
                          borderColor: 'rgba(255,255,255,0.5)',
                          scale: 1,
                        }
                    }
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                  />
                </button>
              ))}
            </nav>
          </div>
        )}
      </AnimatePresence>
    </Provider>
  );
};

export default App;
